import { Navigate, Route, Routes } from "react-router-dom";

import { OrganisationPermissionConstants } from "../constants";
import {
  ActivityPageParams,
  PrivateActivityDocumentsParams,
  PrivateProjectDocumentsParams,
  PublicDeveloperDetailsPreviewParams,
  PublicProjectOverviewPreviewParams,
} from "../models";
import {
  ActivitiesPage,
  ActivityEditPage,
  ActivityViewPage,
  ActivityWizardProvider,
} from "../route/developer/activities";
import ActivitiesTab from "../route/developer/activities/components/ActivitiesTab";
import { DashboardPage } from "../route/developer/dashboard";
import { MarketingStatsPage } from "../route/developer/marketing";
import { ChangePasswordPage, ChangePersonalDetailsPage } from "../route/developer/profile";
import {
  MarketingSettingsTab,
  ProjectAccessAcceptInvitationPage,
  ProjectAccessDetailsPage,
  ProjectAccessInvitePage,
  ProjectAccessTab,
  ProjectPage,
} from "../route/developer/projects";
import { SettingsPage, SettingsProjectAccessInvitePage, SettingsProjectAccessTab } from "../route/developer/settings";
import { DocumentTypesTab, DocumentTypeViewPage } from "../route/developer/settings/document-types";
import { EmbeddingTab } from "../route/developer/settings/embedding";
import { DeveloperMarketingAssetsTab } from "../route/developer/settings/marketing-assets";
import { OrganisationTab } from "../route/developer/settings/organisation";
import { UsersEditPage, UsersListPage, UsersSendInvitationPage } from "../route/developer/settings/users";
import { PublicPage } from "../route/public";
import { PublicDeveloperDetailsPreviewPage } from "../route/public/developer";
import { PublicProjectOverviewPreviewPage, PublicProjectPreviewContainer } from "../route/public/project";
import {
  ActivityDashboardProvider,
  ActivityDiscussionProvider,
  ActivityDiscussionTab,
  ActivityPage,
  ActivityTasksTab,
  DocumentsTab as ActivityDocumentsTab,
  VersionHistoryTab,
} from "../route/shared/activities-dashboard";
import { ActivityDocumentHistoryPage } from "../route/shared/activities-dashboard/documents";
import { Error403Page, Error404Page, Error500Page } from "../route/shared/errors";
import { SharedInventoryManagementBoard } from "../route/shared/inventory";
import {
  LandholdingDetailsTab,
  LandholdingDocumentsTab,
  LandholdingInventoryTab,
  LandholdingLocationTab,
  LandholdingPage,
  LandholdingProvider,
  LandholdingTasksTab,
} from "../route/shared/landholding";
import {
  DocumentProvider,
  DocumentsTab,
  GroupProvider,
  LocationTab,
  OverviewTab,
  ProjectActivitiesTab,
  ProjectAuditHistoryPage,
  ProjectDetailsTab,
  ProjectDocumentHistoryPage,
  ProjectOverviewManageProjectGroupPage,
  ProjectProvider,
  SharedProjectsPage,
} from "../route/shared/projects";
import { InventoryTab, TasksTab } from "../route/shared/projects/project";
import { ProjectRoadmapPage } from "../route/shared/projects/project-roadmap";
import { ProjectTrackerPage } from "../route/shared/projects/project-tracker";
import { AuditHistoryTab } from "../route/shared/settings/audit-history";
import {
  SharedWorkflowTemplatePage,
  SharedWorkflowTemplatesPage,
  TasksBoardPage,
  TasksBoardProvider,
  WorkflowTemplateProvider,
} from "../route/shared/workflows";
import { TagsProvider } from "../templates/TagsPanel/TagsContext";
import { useRouteGuards } from "./guards";

export const DeveloperRoutes = (): JSX.Element => {
  const { RequireUserPermission, RequireProjectPermission, RequireDeveloperAuth } = useRouteGuards();

  return (
    <Routes>
      <Route
        path="dashboard"
        element={
          <RequireDeveloperAuth>
            <DashboardPage />
          </RequireDeveloperAuth>
        }
      />
      <Route
        path="activities"
        element={
          <RequireDeveloperAuth>
            <ActivitiesPage />
          </RequireDeveloperAuth>
        }
      >
        <Route path="in_progress" element={<ActivitiesTab />} />
        <Route path="in_review" element={<ActivitiesTab />} />
        <Route path="completed" element={<ActivitiesTab />} />
        <Route path="" element={<Navigate to="in_progress" replace />} />
      </Route>
      <Route
        path={`activities/:${ActivityPageParams.activityHistoryUuid}/edit`}
        element={
          <RequireDeveloperAuth>
            <ActivityWizardProvider>
              <ActivityEditPage />
            </ActivityWizardProvider>
          </RequireDeveloperAuth>
        }
      />
      <Route
        path={`activities/:${ActivityPageParams.activityHistoryUuid}/view`}
        element={
          <RequireDeveloperAuth>
            <ActivityWizardProvider>
              <ActivityViewPage />
            </ActivityWizardProvider>
          </RequireDeveloperAuth>
        }
      />

      <Route
        path={`activity/:${ActivityPageParams.activityUuid}/document/:${PrivateActivityDocumentsParams.activityDocumentUuid}`}
        element={
          <RequireDeveloperAuth>
            <ActivityDashboardProvider>
              <ActivityDocumentHistoryPage />
            </ActivityDashboardProvider>
          </RequireDeveloperAuth>
        }
      />

      <Route
        path={`activity/:${ActivityPageParams.activityUuid}/*`}
        element={
          <ActivityDashboardProvider>
            <ActivityPage />
          </ActivityDashboardProvider>
        }
      >
        <Route
          path="discussions"
          element={
            <ActivityWizardProvider>
              <RequireDeveloperAuth>
                <ActivityDiscussionProvider>
                  <ActivityDiscussionTab />
                </ActivityDiscussionProvider>
              </RequireDeveloperAuth>
            </ActivityWizardProvider>
          }
        />
        <Route
          path="documents"
          element={
            <RequireDeveloperAuth>
              <ActivityDocumentsTab />
            </RequireDeveloperAuth>
          }
        />
        <Route
          path="history"
          element={
            <ActivityWizardProvider>
              <RequireDeveloperAuth>
                <VersionHistoryTab />
              </RequireDeveloperAuth>
            </ActivityWizardProvider>
          }
        />
        <Route
          path="tasks"
          element={
            <RequireDeveloperAuth>
              <ActivityTasksTab />
            </RequireDeveloperAuth>
          }
        />
      </Route>
      <Route
        path="inventory"
        element={
          <RequireDeveloperAuth>
            <TagsProvider>
              <SharedInventoryManagementBoard />
            </TagsProvider>
          </RequireDeveloperAuth>
        }
      />
      <Route
        path="projects/*"
        element={
          <RequireDeveloperAuth>
            <SharedProjectsPage />
          </RequireDeveloperAuth>
        }
      >
        <Route
          path="project-tracker"
          element={
            <RequireDeveloperAuth>
              <ProjectTrackerPage />
            </RequireDeveloperAuth>
          }
        />
        <Route
          path="project-roadmap"
          element={
            <RequireDeveloperAuth>
              <ProjectRoadmapPage />
            </RequireDeveloperAuth>
          }
        />
        <Route path="" element={<Navigate to="project-tracker" replace />} />
      </Route>
      <Route
        path="projects/:projectUuid/*"
        element={
          <ProjectProvider>
            <TagsProvider>
              <ProjectPage />
            </TagsProvider>
          </ProjectProvider>
        }
      >
        <Route
          path="overview"
          element={
            <RequireDeveloperAuth>
              <OverviewTab />
            </RequireDeveloperAuth>
          }
        />
        <Route
          path="details"
          element={
            <RequireDeveloperAuth>
              <ProjectDetailsTab />
            </RequireDeveloperAuth>
          }
        />
        <Route
          path="location"
          element={
            <RequireDeveloperAuth>
              <LocationTab />
            </RequireDeveloperAuth>
          }
        />
        <Route
          path="inventory"
          element={
            <RequireDeveloperAuth>
              <InventoryTab />
            </RequireDeveloperAuth>
          }
        />
        <Route
          path="activities"
          element={
            <RequireDeveloperAuth>
              <ProjectActivitiesTab />
            </RequireDeveloperAuth>
          }
        />
        <Route
          path="documents"
          element={
            <RequireDeveloperAuth>
              <DocumentsTab />
            </RequireDeveloperAuth>
          }
        />
        <Route
          path="marketing-settings"
          element={
            <RequireDeveloperAuth>
              <MarketingSettingsTab />
            </RequireDeveloperAuth>
          }
        />
        <Route
          path="access"
          element={
            <RequireDeveloperAuth>
              <RequireProjectPermission>
                <ProjectAccessTab />
              </RequireProjectPermission>
            </RequireDeveloperAuth>
          }
        />
        <Route
          path="tasks"
          element={
            <RequireDeveloperAuth>
              <TasksTab />
            </RequireDeveloperAuth>
          }
        />
      </Route>
      <Route
        path="projects/:projectUuid/audit-history"
        element={
          <RequireDeveloperAuth>
            <ProjectProvider>
              <ProjectAuditHistoryPage />
            </ProjectProvider>
          </RequireDeveloperAuth>
        }
      />
      <Route
        path="projects/:groupUuid/manage-project-group"
        element={
          <RequireDeveloperAuth>
            <GroupProvider>
              <ProjectOverviewManageProjectGroupPage />
            </GroupProvider>
          </RequireDeveloperAuth>
        }
      />
      <Route
        path="landholding/:groupUuid/manage-landholding"
        element={
          <RequireDeveloperAuth>
            <GroupProvider>
              <ProjectOverviewManageProjectGroupPage />
            </GroupProvider>
          </RequireDeveloperAuth>
        }
      />
      <Route
        path="project/access-details/:projectOrganisationInvitationUuid/:projectUuid"
        element={
          <RequireDeveloperAuth>
            <ProjectProvider>
              <ProjectAccessDetailsPage />
            </ProjectProvider>
          </RequireDeveloperAuth>
        }
      />
      <Route
        path="projects/:projectUuid/access/invite"
        element={
          <RequireDeveloperAuth>
            <ProjectProvider>
              <ProjectAccessInvitePage />
            </ProjectProvider>
          </RequireDeveloperAuth>
        }
      />
      <Route
        path="project/access/invite/accept/:projectOrganisationInvitationUuid"
        element={
          <RequireDeveloperAuth>
            <ProjectProvider>
              <ProjectAccessAcceptInvitationPage />
            </ProjectProvider>
          </RequireDeveloperAuth>
        }
      />
      <Route
        path={`projects/:${PrivateProjectDocumentsParams.projectUuid}/documents/:${PrivateProjectDocumentsParams.projectDocumentUuid}`}
      >
        <Route
          path=""
          element={
            <RequireDeveloperAuth>
              <ProjectProvider>
                <DocumentProvider>
                  <ProjectDocumentHistoryPage />
                </DocumentProvider>
              </ProjectProvider>
            </RequireDeveloperAuth>
          }
        />
      </Route>
      <Route
        path={`activities/:${ActivityPageParams.activityHistoryUuid}/view?:location`}
        element={
          <RequireDeveloperAuth>
            <ActivityViewPage />
          </RequireDeveloperAuth>
        }
      />
      <Route
        path="workflows/templates"
        element={
          <RequireDeveloperAuth>
            <SharedWorkflowTemplatesPage />
          </RequireDeveloperAuth>
        }
      />
      <Route
        path="workflows/templates/:workflowTemplateUuid"
        element={
          <WorkflowTemplateProvider>
            <RequireDeveloperAuth>
              <SharedWorkflowTemplatePage />
            </RequireDeveloperAuth>
          </WorkflowTemplateProvider>
        }
      />
      <Route
        path="workflows/tasks-board/:workflowUuid"
        element={
          <TasksBoardProvider>
            <RequireDeveloperAuth>
              <TasksBoardPage />
            </RequireDeveloperAuth>
          </TasksBoardProvider>
        }
      />
      <Route
        path="landholding/:landholdingUuid/*"
        element={
          <LandholdingProvider>
            <TagsProvider>
              <LandholdingPage />
            </TagsProvider>
          </LandholdingProvider>
        }
      >
        <Route
          path="details"
          element={
            <RequireDeveloperAuth>
              <LandholdingDetailsTab />
            </RequireDeveloperAuth>
          }
        />
        <Route
          path="location"
          element={
            <RequireDeveloperAuth>
              <LandholdingLocationTab />
            </RequireDeveloperAuth>
          }
        />
        <Route
          path="inventory"
          element={
            <RequireDeveloperAuth>
              <LandholdingInventoryTab />
            </RequireDeveloperAuth>
          }
        />
        <Route
          path="documents"
          element={
            <RequireDeveloperAuth>
              <LandholdingDocumentsTab />
            </RequireDeveloperAuth>
          }
        />
        <Route
          path="tasks"
          element={
            <RequireDeveloperAuth>
              <LandholdingTasksTab />
            </RequireDeveloperAuth>
          }
        />
      </Route>
      <Route
        path="settings/*"
        element={
          <RequireDeveloperAuth>
            <SettingsPage />
          </RequireDeveloperAuth>
        }
      >
        <Route
          path="users"
          element={
            <RequireUserPermission
              userPermission={OrganisationPermissionConstants.READ_ORGANISATION_USERS}
              redirectRoute="/d/settings/organisation"
            >
              <UsersListPage />
            </RequireUserPermission>
          }
        />
        <Route path="organisation" element={<OrganisationTab />} />
        <Route path="marketing-assets" element={<DeveloperMarketingAssetsTab />} />
        <Route path="marketing-assets/:previewUuid" element={<DeveloperMarketingAssetsTab />} />
        <Route path="embedding" element={<EmbeddingTab />} />
        <Route path="project-access" element={<SettingsProjectAccessTab />} />
        <Route path="document-types" element={<DocumentTypesTab />} />
        <Route path="audit-history" element={<AuditHistoryTab />} />
        <Route path="" element={<Navigate to="users" replace />} />
      </Route>
      <Route
        path="settings/users/send-invitation"
        element={
          <RequireDeveloperAuth>
            <UsersSendInvitationPage />
          </RequireDeveloperAuth>
        }
      />
      <Route
        path="settings/users/:userUuid/edit"
        element={
          <RequireDeveloperAuth>
            <UsersEditPage />
          </RequireDeveloperAuth>
        }
      />
      <Route
        path="settings/document-types/:typeUuid/view"
        element={
          <RequireDeveloperAuth>
            <DocumentTypeViewPage />
          </RequireDeveloperAuth>
        }
      />
      <Route
        path="settings/project-access/invite"
        element={
          <RequireDeveloperAuth>
            <SettingsProjectAccessInvitePage />
          </RequireDeveloperAuth>
        }
      />
      <Route
        path="marketing/marketing-stats"
        element={
          <RequireDeveloperAuth>
            <MarketingStatsPage />
          </RequireDeveloperAuth>
        }
      />
      <Route
        path="profile/change-password"
        element={
          <RequireDeveloperAuth>
            <ChangePasswordPage />
          </RequireDeveloperAuth>
        }
      />
      <Route
        path="profile/change-personal-details"
        element={
          <RequireDeveloperAuth>
            <ChangePersonalDetailsPage />
          </RequireDeveloperAuth>
        }
      />
      <Route
        path={`developer/details/preview/:${PublicDeveloperDetailsPreviewParams.previewUuid}`}
        element={
          <RequireDeveloperAuth>
            <PublicDeveloperDetailsPreviewPage />
          </RequireDeveloperAuth>
        }
      />
      <Route path="project/*" element={<PublicPage />}>
        <Route
          path={`:${PublicProjectOverviewPreviewParams.uuid}/preview/:${PublicProjectOverviewPreviewParams.previewUuid}`}
          element={
            <RequireDeveloperAuth>
              <PublicProjectPreviewContainer />
            </RequireDeveloperAuth>
          }
        >
          <Route path="overview" element={<PublicProjectOverviewPreviewPage />} />
        </Route>
      </Route>
      <Route
        path="403/access-denied"
        element={
          <RequireDeveloperAuth>
            <Error403Page />
          </RequireDeveloperAuth>
        }
      />
      <Route
        path="500/application-error"
        element={
          <RequireDeveloperAuth>
            <Error500Page />
          </RequireDeveloperAuth>
        }
      />
      <Route
        path="*"
        element={
          <RequireDeveloperAuth>
            <Error404Page />
          </RequireDeveloperAuth>
        }
      />
    </Routes>
  );
};
